import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import OrderForm from '../components/OrderForm';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';
import { cn, getResponsiveCardSpan } from '../lib/utils';
import {
  fetchCollection,
  fetchSide,
  fetchVirksomhedsoplysninger,
  FALLBACK_YDELSER,
  FALLBACK_REFERENCER,
  FALLBACK_GALLERI,
  FALLBACK_SIDER,
  FALLBACK_VIRKSOMHED,
  getSlug
} from '../lib/cms';

export default function Forside() {
  const [ydelser, setYdelser] = useState(FALLBACK_YDELSER);
  const [referencer, setReferencer] = useState(FALLBACK_REFERENCER);
  const [gallerier, setGallerier] = useState(FALLBACK_GALLERI);
  const [sideData, setSideData] = useState(FALLBACK_SIDER.forside);
  const [virksomhed, setVirksomhed] = useState(FALLBACK_VIRKSOMHED);

  // Form states for restyled contact and newsletter sections
  const [contactStatus, setContactStatus] = useState('idle'); // idle | submitting | success | error
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | submitting | success | error

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCaptions, setLightboxCaptions] = useState([]);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Fejl');
      setContactStatus('success');
      form.reset();
    } catch {
      setContactStatus('error');
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Fejl');
      setNewsletterStatus('success');
      form.reset();
    } catch {
      setNewsletterStatus('error');
    }
  };

  useEffect(() => {
    async function loadContent() {
      const [ydData, refData, galData, sideRes, virkRes] = await Promise.all([
        fetchCollection('ydelser', FALLBACK_YDELSER),
        fetchCollection('referencer', FALLBACK_REFERENCER),
        fetchCollection('galleri', FALLBACK_GALLERI),
        fetchSide('forside'),
        fetchVirksomhedsoplysninger()
      ]);
      if (Array.isArray(ydData) && ydData.length > 0) setYdelser(ydData);
      if (Array.isArray(refData) && refData.length > 0) setReferencer(refData);
      if (Array.isArray(galData) && galData.length > 0) setGallerier(galData);
      if (sideRes) setSideData(sideRes);
      if (virkRes) setVirksomhed(virkRes);
    }
    loadContent();
  }, []);

  const openLightbox = (imgs, captions = [], index = 0) => {
    setLightboxImages(imgs);
    setLightboxCaptions(captions);
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  // Hero image
  const heroImage = sideData.billede?.[0] || 'https://assets.auxodev.dk/assets/22e5a7c6-e40d-4b26-8ee0-cb7d2891cbe9/imgi-21-646887628-122121748119136715-5214852611206569786-n.jpg';
  const ctaSecondary = sideData.sekundaer_cta_tekst || `Ring på ${virksomhed.telefon}`;
  const trustBadges = sideData.fordele ? sideData.fordele.split('|').map((s) => s.trim()).filter(Boolean) : ['Direkte kontakt', 'Skræddersyet efter mål', 'Uforpligtende tilbud'];

  return (
    <div className="bg-slate-50 text-slate-900">
      <PageMeta
        title={sideData.titel || "Duus Rustfri"}
        description={sideData.meta_beskrivelse || "Duus Rustfri – Enkeltmandsvirksomhed i Vissenbjerg. Alt fra små reparationer til specialløsninger i rustfrit stål, sort stål og aluminium. Kontakt for et uforpligtende tilbud."}
      />

      {/* 1. HERO SECTION */}
      <section className="relative bg-slate-950 text-white overflow-hidden border-b border-slate-800 -mt-20 sm:-mt-[112px]">
        {/* Background photo with transparent overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl(heroImage, 'width=1920,quality=85,format=auto')}
            alt="Duus Rustfri værksted - Svejset stålrør i skruestik med præcisionsmåling"
            className="w-full h-full object-cover object-center scale-105 transition-transform duration-1000"
          />
          {/* Dark transparent overlay with vignette and top gradient for header clarity */}
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-black/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 lg:pt-52 pb-20 sm:pb-28 lg:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Hero Text Column */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] drop-shadow-md">
                Specialløsninger og godt håndværk i{' '}
                <span className="bg-gradient-to-r from-slate-100 via-sky-200 to-sky-400 bg-clip-text text-transparent">
                  rustfrit stål, sort & alu
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-slate-200 max-w-2xl leading-relaxed drop-shadow-sm">
                Jeg sætter en ære i godt håndværk og tilbyder alt fra små reparationer til skræddersyede specialløsninger, foderkrybber og præcisionssvejsning for private og erhverv.
              </p>

              {/* CTAs (Specific prompt requirement) */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                <a
                  href={sideData.cta_link || "#bestil"}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-7 py-4 rounded-xl bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-500 hover:to-blue-600 text-white font-semibold text-base shadow-xl shadow-sky-950/60 transition transform hover:-translate-y-0.5 active:translate-y-0 text-center cursor-pointer"
                >
                  <span>{sideData.cta_tekst || "Send en bestillingsforespørgsel"}</span>
                  <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
                </a>

                <a
                  href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-white font-semibold text-base border border-slate-700 hover:border-slate-600 transition whitespace-nowrap text-center cursor-pointer"
                >
                  <i className="fa-solid fa-phone text-sky-400" aria-hidden="true" />
                  <span className="whitespace-nowrap">{ctaSecondary}</span>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="pt-6 grid grid-cols-3 gap-3 border-t border-slate-800/80 text-xs sm:text-sm text-slate-300">
                {trustBadges.map((badge, bIdx) => (
                  <div key={bIdx} className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Hero Image Card */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl p-2 bg-gradient-to-b from-slate-700/50 to-slate-900/80 border border-slate-700 shadow-2xl">
                <div className="relative rounded-xl overflow-hidden aspect-[4/3] group cursor-pointer" onClick={() => openLightbox([heroImage], ['Svejset stålrør i skruestik med vaterpas og fittings på værkstedet'])}>
                  <img
                    src={imageUrl(heroImage, 'width=800,quality=90,format=auto')}
                    alt="Svejset stålrør på arbejdsbord i værksted"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                    <div>
                      <p className="text-xs uppercase font-mono tracking-wider text-sky-400">Værksted & Montage</p>
                      <p className="text-sm font-semibold">Præcision i hver eneste svejsning</p>
                    </div>
                    <span className="w-9 h-9 rounded-full bg-slate-900/80 border border-slate-700 flex items-center justify-center text-xs">
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                    </span>
                  </div>
                </div>

                {/* Floating badge */}
                <div className="mt-3 px-4 py-3 bg-slate-900/90 rounded-lg border border-slate-800 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-300">
                    <i className="fa-solid fa-location-dot text-sky-400" aria-hidden="true" />
                    <span>{virksomhed.adresse}, {virksomhed.postnr_by}</span>
                  </div>
                  <span className="font-semibold text-sky-400">{virksomhed.omraade || 'Fyn & hele landet'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. INTRO: HVEM DUUS RUSTFRI ER */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-user-gear text-sky-600" aria-hidden="true" />
                <span>{sideData.sektion_2_badge || 'Om virksomheden'}</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-950 tracking-tight leading-tight">
                {sideData.sektion_2_overskrift || 'Enkeltmandsvirksomhed med passion for solidt håndværk'}
              </h2>

              {sideData.sektion_2_tekst ? (
                <div
                  className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base"
                  dangerouslySetInnerHTML={{ __html: sideData.sektion_2_tekst }}
                />
              ) : (
                <>
                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                    Hos Duus Rustfri er du sikret direkte kontakt fra start til slut. Ingen fordyrende mellemled eller bureaukrati – du taler direkte med håndværkeren, der udfører opgaven.
                  </p>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                    Jeg sætter en ære i veludført arbejde og anvender kun kvalitetsmaterialer inden for rustfrit stål, sort stål og aluminium. Uanset om opgaven gælder en hurtig reparation af en knækket komponent eller udvikling af en komplet specialløsning, går jeg til arbejdet med samme faglige omhu.
                  </p>
                </>
              )}

              <div className="pt-2">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 font-semibold text-sky-700 hover:text-sky-800 transition"
                >
                  <span>Læs mere om Duus Rustfri</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <div
                className="relative rounded-xl overflow-hidden aspect-[4/5] bg-slate-100 border border-slate-200 shadow-md group cursor-pointer"
                onClick={() => openLightbox(['https://assets.auxodev.dk/assets/22e5a7c6-e40d-4b26-8ee0-cb7d2891cbe9/imgi-21-648091554-122121748029136715-4881053340309711990-n.jpg'], ['Værksted med sorterede komponenter, rør og fittings'])}
              >
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/22e5a7c6-e40d-4b26-8ee0-cb7d2891cbe9/imgi-21-648091554-122121748029136715-4881053340309711990-n.jpg', 'width=500,quality=85,format=auto')}
                  alt="Metalkomponenter og fittings på værkstedsbord"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-4 text-white text-xs font-medium">
                  Klik for at forstørre
                </div>
              </div>

              <div
                className="relative rounded-xl overflow-hidden aspect-[4/5] bg-slate-100 border border-slate-200 shadow-md group cursor-pointer mt-8"
                onClick={() => openLightbox(['https://assets.auxodev.dk/assets/22e5a7c6-e40d-4b26-8ee0-cb7d2891cbe9/imgi-21-646865653-122121748077136715-5791897527669142794-n.jpg'], ['Svejste cylindriske stålemner med kuppeltoppe'])}
              >
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/22e5a7c6-e40d-4b26-8ee0-cb7d2891cbe9/imgi-21-646865653-122121748077136715-5791897527669142794-n.jpg', 'width=500,quality=85,format=auto')}
                  alt="Svejste cylindriske stålemner"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-4 text-white text-xs font-medium">
                  Klik for at forstørre
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. YDELSER & PRODUKTER OVERBLIK */}
      <section className="py-16 sm:py-24 bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold uppercase tracking-wider mb-3">
              <i className="fa-solid fa-cubes-stacked text-sky-600" aria-hidden="true" />
              <span>Hvad vi tilbyder</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
              Ydelser & specialfremstilling
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
              Vi arbejder med rustfrit stål, sort stål og aluminium. Alle opgaver tilpasses dine præcise specifikationer.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-6 sm:gap-8 items-stretch">
            {ydelser.map((ydelse, idx) => {
              const img = ydelse.billede?.[0] || heroImage;
              const spanClass = getResponsiveCardSpan(idx, ydelser.length);
              return (
                <div
                  key={ydelse.id || ydelse.slug}
                  className={`h-full min-w-0 ${spanClass} bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group`}
                >
                  <div
                    className="relative h-56 sm:h-60 w-full flex-shrink-0 overflow-hidden bg-slate-900 cursor-pointer"
                    onClick={() => openLightbox([img], [ydelse.titel])}
                  >
                    <img
                      src={imageUrl(img, 'width=600,quality=80,format=auto')}
                      alt={ydelse.titel}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 right-3 bg-slate-950/80 text-white text-xs px-2.5 py-1 rounded-md border border-slate-700 flex items-center gap-1.5 backdrop-blur-sm">
                      <i className="fa-solid fa-magnifying-glass-plus text-[10px]" aria-hidden="true" />
                      <span>Se foto</span>
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-950 group-hover:text-sky-700 transition">
                        <Link to={`/ydelser/${getSlug(ydelse)}`}>
                          {ydelse.titel}
                        </Link>
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed">
                        {ydelse.kort_beskrivelse}
                      </p>
                    </div>

                    <div className="space-y-4 pt-2">
                      {ydelse.materialer && (
                        <div className="pt-2 border-t border-slate-100 text-xs text-slate-500 min-h-[2.5rem]">
                          <span className="font-semibold text-slate-700">Materialer:</span> {ydelse.materialer}
                        </div>
                      )}

                      <div className="pt-2 flex items-center justify-between border-t border-slate-100">
                        <Link
                          to={`/ydelser/${getSlug(ydelse)}`}
                          className="text-sm font-semibold text-sky-700 hover:text-sky-800 transition inline-flex items-center gap-1.5"
                        >
                          <span>Læs mere</span>
                          <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                        </Link>

                        <a
                          href="#bestil"
                          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 transition"
                        >
                          Forespørg her
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-semibold text-sm transition shadow-md"
            >
              <span>Se alle ydelser i detaljer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. UDVALGTE PROJEKTER & REFERENCER */}
      <section className="py-16 sm:py-24 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-3">
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                <span>Dokumenteret erfaring</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Udvalgte projekter & smedearbejde
              </h2>
            </div>
            <Link
              to="/referencer"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-semibold text-sm transition"
            >
              <span>Se alle referencer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-12 gap-6 sm:gap-8">
            {referencer.slice(0, 3).map((ref, index) => {
              const img = ref.billede?.[0] || heroImage;
              return (
                <div
                  key={ref.id || ref.slug}
                  className={cn(
                    "bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden flex flex-col group hover:border-slate-700 transition duration-300 min-w-0",
                    getResponsiveCardSpan(index, Math.min(referencer.length, 3))
                  )}
                >
                  <div
                    className="relative aspect-[16/11] overflow-hidden bg-slate-950 cursor-pointer"
                    onClick={() => openLightbox(ref.billede || [img], [ref.titel])}
                  >
                    <img
                      src={imageUrl(img, 'width=600,quality=85,format=auto')}
                      alt={ref.titel}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-slate-950/80 px-2.5 py-1 rounded text-xs text-sky-400 font-medium border border-slate-800 backdrop-blur-sm">
                      {ref.opgavetype}
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-white group-hover:text-sky-400 transition">
                        <Link to={`/referencer/${getSlug(ref)}`}>{ref.titel}</Link>
                      </h3>
                      <div
                        className="text-xs sm:text-sm text-slate-400 line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: ref.beskrivelse }}
                      />
                    </div>

                    <div className="pt-3 border-t border-slate-800 text-xs text-slate-500 flex items-center justify-between">
                      <span>{ref.lokation || 'Fyn'}</span>
                      <span className="text-slate-400">{ref.materiale}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. RESTYLED CONTACT & NEWSLETTER SECTION (MATCHING DESIGN REFERENCE) */}
      <section id="bestil" className="relative scroll-mt-20 pt-16 sm:pt-24 bg-slate-100 overflow-hidden">
        {/* Upper split contact card container */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200/90 overflow-hidden grid grid-cols-1 lg:grid-cols-12">
            {/* Left column: Solid light surface with no transparency */}
            <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 bg-white flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-wider mb-4 w-fit">
                <i className="fa-solid fa-paper-plane text-sky-600" aria-hidden="true" />
                <span>Kontakt & Bestilling</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-950 tracking-tight mb-3">
                Send en bestillingsforespørgsel
              </h2>

              <p className="text-sm sm:text-base text-slate-600 mb-8 leading-relaxed">
                Udfyld formularen med dine ønsker eller projektbeskrivelse. Vi vender hurtigt tilbage med en uforpligtende vurdering.
              </p>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                {/* Antispam honeypot */}
                <input
                  type="text"
                  name="website_url"
                  className="sr-only absolute -left-[9999px]"
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* First four fields arranged in a two-column grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="navn" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Navn *
                    </label>
                    <input
                      id="navn"
                      type="text"
                      name="navn"
                      required
                      placeholder="Dit fulde navn"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      E-mail *
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefon" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Telefon *
                    </label>
                    <input
                      id="telefon"
                      type="tel"
                      name="telefon"
                      required
                      placeholder="F.eks. 30 27 16 71"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition"
                    />
                  </div>

                  <div>
                    <label htmlFor="opgavetype" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Opgavetype
                    </label>
                    <select
                      id="opgavetype"
                      name="opgavetype"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition"
                    >
                      <option value="Specialfremstilling">Specialfremstilling</option>
                      <option value="Svejsning & rørarbejde">Svejsning & rørarbejde</option>
                      <option value="Reparation">Reparation</option>
                      <option value="Andet">Andet</option>
                    </select>
                  </div>
                </div>

                {/* Message field spans full width */}
                <div>
                  <label htmlFor="besked" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Besked / Projektbeskrivelse *
                  </label>
                  <textarea
                    id="besked"
                    name="besked"
                    rows={4}
                    required
                    placeholder="Beskriv din opgave, mål, materialeønsker eller spørgsmål..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition resize-y"
                  />
                </div>

                {/* Dominant CTA button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={contactStatus === 'submitting'}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm shadow-md shadow-sky-600/25 transition transform active:scale-[0.99] cursor-pointer disabled:opacity-60"
                  >
                    <span>{contactStatus === 'submitting' ? 'Sender forespørgsel...' : 'Send forespørgsel'}</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </button>

                  {contactStatus === 'success' && (
                    <p className="mt-3 text-sm text-emerald-600 font-medium flex items-center gap-2">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                      Tak for din henvendelse! Vi vender tilbage hurtigst muligt.
                    </p>
                  )}

                  {contactStatus === 'error' && (
                    <p className="mt-3 text-sm text-red-600 font-medium flex items-center gap-2">
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                      Der opstod en fejl. Prøv venligst igen eller ring direkte på 30 27 16 71.
                    </p>
                  )}
                </div>
              </form>
            </div>

            {/* Right column: Full-height image edge-to-edge inside card */}
            <div className="lg:col-span-5 relative min-h-[360px] lg:min-h-full bg-slate-900 overflow-hidden">
              <img
                src={imageUrl(sideData.sekundaert_billede?.[0] || '/_assets/imgi-21-699751925-122129448003136715-150113504640350210-n.jpg', 'width=1000,quality=90,format=auto')}
                alt="Professionel rådgivning og service"
                className="w-full h-full object-cover object-center"
              />

              {/* Floating callout card near bottom */}
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded-xl bg-white/95 backdrop-blur-md shadow-xl border border-slate-200/90 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-sky-600/30">
                  <i className="fa-solid fa-phone-volume text-lg" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-slate-900 uppercase tracking-wider">Direkte kontakt</div>
                  <div className="text-xs text-slate-500 font-medium truncate">Ring til os: {virksomhed.telefon}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lower full-width dark primary newsletter background band */}
        <div className="w-full bg-[#000300] -mt-36 sm:-mt-44 pt-48 sm:pt-56 pb-16 sm:pb-20 border-t border-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              {/* Left side: refined heading / strong body with short supporting sentence */}
              <div className="max-w-xl">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  {sideData.sektion_3_overskrift || 'Hold dig opdateret med nye projekter'}
                </h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  {sideData.sektion_3_tekst || 'Få opdateringer om specialløsninger, nyt fra værkstedet og gode råd direkte i din indbakke.'}
                </p>
              </div>

              {/* Right side: single-line email input and compact CTA button */}
              <form onSubmit={handleNewsletterSubmit} className="w-full lg:max-w-md">
                <input
                  type="text"
                  name="website_url"
                  className="sr-only absolute -left-[9999px]"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div className="flex items-center gap-2 p-1.5 rounded-xl bg-slate-900 border border-slate-800 focus-within:border-sky-500 transition">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Indtast din e-mailadresse"
                    className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'submitting'}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-semibold text-xs sm:text-sm transition flex-shrink-0 cursor-pointer disabled:opacity-60 shadow-sm"
                  >
                    <span>{newsletterStatus === 'submitting' ? 'Tilmeldt...' : 'Tilmeld'}</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </button>
                </div>
                {newsletterStatus === 'success' && (
                  <p className="mt-2 text-xs text-emerald-400 font-medium flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    Tak for din tilmelding!
                  </p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="mt-2 text-xs text-red-400 font-medium flex items-center gap-1.5">
                    <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                    Kunne ikke tilmelde lige nu. Prøv venligst igen.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        captions={lightboxCaptions}
        currentIndex={currentImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setCurrentImgIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
        onSelectIndex={(idx) => setCurrentImgIndex(idx)}
      />
    </div>
  );
}
